import styled from "@emotion/styled";
import { LocalTime, ZoneId } from "@js-joda/core";
import React from "react";
import { Page } from "../../../activities/AppPage";
import { useAutosizeTextarea } from "../../../hooks/useAutoresize";
import { isLoading, Loading } from "../../../Loading";
import {
  CommCenterMessageActionId,
  CommCenterTicketId,
  HRQuestionId,
  HRSectionId,
  HRSubSectionId,
  VisitInstanceDetails,
} from "../../../messages";
import {
  CaregiverCommCenterMessage,
  CommCenterMessage,
  CommCenterTicket,
} from "../../../messages/CommunicationCenter";
import { STRINGS } from "../../../strings";
import { Visit } from "../../../system/AppState/AppVisitsState";
import { translate } from "../../../translate";
import { KeyedList } from "../../../utils/ListImpl";
import ScaffoldTitle from "../../Scaffold/components/ScaffoldTitle";
import Scaffold from "../../Scaffold/Scaffold";
import {
  buildSendMessagePayload,
  CommCenterListActionItem,
  parseChatMessageTicks,
  TypingPerson,
} from "../utils/chatUtils";
import ChatAvatar from "./ChatAvatar";
import ChatMessage from "./ChatMessage";
import ChatRoomFooter from "./ChatRoomFooter";
import ChatRoomTypingIndicator from "./ChatRoomTypingIndicator";
import VisitBroadcastExtraHeader from "./VisitBroadcastExtraHeader";
import VisitInstanceExtraHeader from "./VisitInstanceExtraHeader";

interface BaseProps {
  visitBroadcasts: Loading | KeyedList<Visit, "id">;
  visitInstances: Loading | KeyedList<VisitInstanceDetails, "id">;
  images: string[];
  documents: string[];
  videos: string[];
  typingPerson: TypingPerson | null;
  containerRef: React.RefObject<HTMLDivElement>;
  imagePreview: string | null;
  onSendMessage: (params: {
    ticketId: CommCenterTicketId;
    message: CaregiverCommCenterMessage;
  }) => void;
  onClickActionItem: (item: CommCenterListActionItem, actionId: CommCenterMessageActionId) => void;
  onClickGoBack: () => void;
  onSelectImages: (images: string[]) => void;
  onSelectDocuments: (documents: string[]) => void;
  onSelectVideos: (videos: string[]) => void;
  onResetImages: () => void;
  onResetDocuments: () => void;
  onResetVideos: () => void;
  onClickRemoveDocument: (document: string) => void;
  onClickRemoveImage: (image: string) => void;
  onClickRemoveVideo: (video: string) => void;
  onRequestImagePreview: (image: string | null) => void;
  onRequestNavigate: (page: Page) => void;
}

type Props =
  | ({
      isLoading: false;
      ticket: CommCenterTicket;
    } & BaseProps)
  | ({
      isLoading: true;
    } & BaseProps);

const inputLineHeight = 20;

function ChatRoomScreen(props: Props) {
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  useAutosizeTextarea(inputRef, { lineHeight: inputLineHeight });

  React.useEffect(() => {
    const current = props.containerRef.current;

    if (current !== null) {
      current.scrollTop = current.scrollHeight;
    }
  }, [props.containerRef]);

  const handleSubmitChatMessage = React.useCallback(
    (message: string) => {
      if (props.isLoading) {
        return;
      }

      const trimmedMessage = message.trim();

      if (trimmedMessage.length === 0 && props.images.length === 0) {
        return;
      }

      const payload = buildSendMessagePayload({
        text: trimmedMessage,
        images: props.images,
        documents: props.documents,
        videos: props.videos,
      });

      props.onSendMessage({
        ticketId: props.ticket.id,
        message: payload,
      });

      const textarea = inputRef.current;

      if (textarea !== null) {
        textarea.value = "";
        textarea.style.height = `${inputLineHeight}px`;
        props.onResetImages();
        props.onResetDocuments();
        props.onResetVideos();
      }
    },
    [props]
  );

  React.useEffect(() => {
    const textarea = inputRef.current;

    if (textarea === null) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();

        handleSubmitChatMessage(textarea.value);
      }
    };

    textarea.addEventListener("keydown", handleKeyDown);

    return () => textarea.removeEventListener("keydown", handleKeyDown);
  }, [handleSubmitChatMessage, inputRef]);

  const showComplianceMessageActionButton = (message: CommCenterMessage) => {
    return message.payload.find((p) => p.type === "ONBOARDING_MESSAGE") !== undefined;
  };

  const getComplianceItemActionItemDetails = (
    message: CommCenterMessage
  ):
    | {
        type: "REJECTED_ITEM";
        subSectionId: HRSubSectionId;
        sectionId: HRSectionId;
        questionId: HRQuestionId;
      }
    | {
        type: "APPROVED_ITEM";
      }
    | null => {
    const onboardingMessagePayload = message.payload.find((p) => p.type === "ONBOARDING_MESSAGE");

    if (onboardingMessagePayload?.type === "ONBOARDING_MESSAGE") {
      const onboaardingMessageItemData = onboardingMessagePayload.payload;

      if (onboaardingMessageItemData.type === "REJECTED_ITEM") {
        const subSectionId =
          onboaardingMessageItemData.item.find((i) => i.entity === "ONBOARDING_SUB_SECTION")?.id ??
          0;
        const sectionId =
          onboaardingMessageItemData.item.find((i) => i.entity === "ONBOARDING_SECTION")?.id ?? 0;
        const questionId =
          onboaardingMessageItemData.item.find((i) => i.entity === "ONBOARDING_QUESTION")?.id ?? 0;

        return {
          type: "REJECTED_ITEM",
          subSectionId: HRSubSectionId.wrap(subSectionId),
          sectionId: HRSectionId.wrap(sectionId),
          questionId: HRQuestionId.wrap(questionId),
        };
      }

      return {
        type: "APPROVED_ITEM",
      };
    }

    return null;
  };

  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.CHAT_WITH_MEDFLYT)} />}
      onClickGoBack={props.onClickGoBack}
      endActions={[<ChatAvatar key="avatar" url={null} />]}
      extraHeader={getExtraHeader(props)}
      footer={
        props.isLoading ? null : (
          <ChatRoomFooter
            ticket={props.ticket}
            inputRef={inputRef}
            images={props.images}
            documents={props.documents}
            videos={props.videos}
            onSelectDocuments={props.onSelectDocuments}
            onSendMessage={props.onSendMessage}
            onSubmitChatMessage={handleSubmitChatMessage}
            onClickActionItem={props.onClickActionItem}
            onSelectImages={props.onSelectImages}
            onClickRemoveImage={props.onClickRemoveImage}
            onClickRemoveDocument={props.onClickRemoveDocument}
            onSelectVideos={props.onSelectVideos}
            onClickRemoveVideo={props.onClickRemoveVideo}
          />
        )
      }
    >
      <ChatRoomScreen.Root ref={props.containerRef}>
        <ChatRoomScreen.MessagesWrapper>
          {props.isLoading
            ? null
            : props.ticket.messages.map((message, i) => {
                if (message.createdBy.type === "Caregiver") {
                  return (
                    <ChatMessage
                      key={message.id.toString()}
                      variant="outgoing"
                      time={LocalTime.ofInstant(message.createdAt, ZoneId.systemDefault())}
                      ticks={parseChatMessageTicks(message)}
                      payload={message.payload}
                      ticketId={props.ticket.id}
                      showActionsList={props.ticket.enableAgencyResponse}
                      isLatestMessage={i === props.ticket.messages.length - 1}
                      imagePreview={props.imagePreview}
                      onSendMessage={props.onSendMessage}
                      onClickActionItem={props.onClickActionItem}
                      onRequestImagePreview={props.onRequestImagePreview}
                      onRequestNavigate={props.onRequestNavigate}
                    />
                  );
                }

                return (
                  <ChatMessage
                    key={message.id.toString()}
                    variant="incoming"
                    avatarUrl={
                      message.createdBy.type === "Agency Member" ? message.createdBy.photoUrl : null
                    }
                    senderName={
                      message.createdBy.type === "Agency Member"
                        ? message.createdBy.name
                        : "Medflyt"
                    }
                    time={LocalTime.ofInstant(message.createdAt, ZoneId.systemDefault())}
                    payload={message.payload}
                    ticketId={props.ticket.id}
                    showActionsList={props.ticket.enableAgencyResponse}
                    isLatestMessage={i === props.ticket.messages.length - 1}
                    imagePreview={props.imagePreview}
                    showComplianceMessageActionButton={showComplianceMessageActionButton(message)}
                    complianceItemActionItemDetails={getComplianceItemActionItemDetails(message)}
                    onSendMessage={props.onSendMessage}
                    onClickActionItem={props.onClickActionItem}
                    onRequestImagePreview={props.onRequestImagePreview}
                    onRequestNavigate={props.onRequestNavigate}
                  />
                );
              })}
          {props.typingPerson !== null ? (
            <ChatRoomTypingIndicator
              avatarUrl={props.typingPerson.avatarUrl}
              senderName={props.typingPerson.name}
            />
          ) : null}
        </ChatRoomScreen.MessagesWrapper>
      </ChatRoomScreen.Root>
    </Scaffold>
  );
}

function getExtraHeader(params: Props) {
  const broadcastVisit = params.isLoading
    ? undefined
    : getBroadcastVisit(params.visitBroadcasts, params.ticket);

  if (broadcastVisit !== undefined) {
    return (
      <VisitBroadcastExtraHeader
        broadcast={broadcastVisit}
        onGoToVisit={() => {
          params.onRequestNavigate({
            type: "AvailableVisit",
            visitBroadcastId: broadcastVisit.id,
          });
        }}
      />
    );
  }

  const visitInstance = params.isLoading
    ? undefined
    : getVisitInstance(params.visitInstances, params.ticket);

  if (visitInstance !== undefined) {
    return (
      <VisitInstanceExtraHeader
        visit={visitInstance}
        onGoToVisit={() => {
          params.onRequestNavigate({
            type: "VisitInstance",
            visitInstanceId: visitInstance.id,
          });
        }}
      />
    );
  }

  return null;
}

function getVisitInstance(
  instances: Loading | KeyedList<VisitInstanceDetails, "id">,
  ticket: CommCenterTicket
) {
  const relatedVisitInstance = ticket.relatedVisitInstance;

  if (relatedVisitInstance === null || isLoading(instances)) {
    return undefined;
  }

  return instances.find(
    (instance): instance is VisitInstanceDetails => instance.id === relatedVisitInstance.id
  );
}

function getBroadcastVisit(broadcasts: Loading | KeyedList<Visit, "id">, ticket: CommCenterTicket) {
  const relatedVisitBroadcast = ticket.relatedVisitBroadcast;

  if (relatedVisitBroadcast === null || isLoading(broadcasts)) {
    return undefined;
  }

  const broadcastVisit = broadcasts.find(
    (broadcast): broadcast is Visit.Available =>
      broadcast.id === relatedVisitBroadcast.id && broadcast.type === "Available"
  );

  return broadcastVisit as Visit.Available | undefined;
}

ChatRoomScreen.Root = styled.div`
  background-color: #f4f7fb;
  padding: 12px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
`;

ChatRoomScreen.MessagesWrapper = styled.div`
  > * + * {
    margin-top: 8px;
  }
`;

export default ChatRoomScreen;
